// @ts-strict-ignore
import React, { useRef } from 'react';
import type { Action, NodeEditorTab, PublishedAttribute } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import type { UseFormProps } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { FormControlMap } from './types';
import { getFormAdapter, getActionTabKey } from './helpers';
import { useAppSelector } from '~/store/reduxHooks';
import { selectWorkflowProps, selectPublishedAttributes } from '~/dashboard/views/workflows/store/redux/selectors';

import type { FormState as InputAttributeConfigurationFormState } from '../AttributeConfiguration/InputAttributeConfigurationTab';
import type { FormState as MatrixOptionsFormState } from '../MatrixOptionsTab';
import type { FormState as OutputAttributeExpressionFormState } from '../OutputAttributeExpressionTab';
import type { FormState as OutputAttributeOptionsFormState } from '../AttributeConfiguration/OutputAttributeOptionsTab';
import type { FormState as ServiceOptionsFormState } from '../ServiceTabs/ServiceOptionsTab';
import type { FormState as ServiceLocalSettingsFormState } from '../ServiceTabs/ServiceLocalSettingsTab';
import type { FormState as TagGlobalSettingsFormState } from '../TagGlobalSettingsTab';
import type { FormState as TagLocalSettingsFormState } from '../TagLocalSettingsTab';

/**
 * Handles all of the form creation logic for all NodeEditorDrawer forms.
 * This hook also deals with resetting a form to its default values whenever a node is focused.
 *
 * @param focusedAction action for which to load a form controller
 * @param tab tab for which to load a form controller
 * @param extraFormProps - Anything you can pass to react-hook-form's `useForm`
 * @returns a form controller for the specified action+tab combination with full type safety.
 */
export default function useNodeEditorForm(
  focusedAction: Action,
  tab: NodeEditorTab,
  extraFormProps?: UseFormProps<any>,
) {
  const workflowProps = useAppSelector(selectWorkflowProps);
  const publishedAttributes = useAppSelector(selectPublishedAttributes);

  // define custom react hook form props
  const formProps: UseFormProps<any> = {
    mode: 'onTouched',
    ...extraFormProps,
  };
  // props are selected here to keep form control in sync with the store
  /**
   * This map indexes an action type + tab combination with a corresponding form controller.
   * A node can have more than one form, as it might have more than one drawer tab with a
   * form in it.
   */
  const formControlMap: FormControlMap = {
    [getActionTabKey(ActionType.INPUT, 'configuration')]: useForm<InputAttributeConfigurationFormState>(formProps),
    [getActionTabKey(ActionType.MATRIX_MODEL, 'options')]: useForm<MatrixOptionsFormState>(formProps),
    [getActionTabKey(ActionType.OUTPUT, 'options')]: useForm<OutputAttributeOptionsFormState>(formProps),
    [getActionTabKey(ActionType.OUTPUT, 'rule')]: useForm<OutputAttributeExpressionFormState>(formProps),
    [getActionTabKey(ActionType.SERVICE, 'options')]: useForm<ServiceOptionsFormState>(formProps),
    [getActionTabKey(ActionType.SERVICE, 'configuration')]: useForm<ServiceLocalSettingsFormState>(formProps), // TODO: update it to 'options' once we clean out the legacy service options
    [getActionTabKey(ActionType.TAG, 'configuration')]: useForm<TagGlobalSettingsFormState>(formProps),
    [getActionTabKey(ActionType.TAG, 'options')]: useForm<TagLocalSettingsFormState>(formProps),
    // add other form controllers here
  };

  const formKey = getActionTabKey(focusedAction?.type, tab);
  const formControl = formControlMap[formKey];

  /**
   * Calls the `reset()` function on the corresponding form controller whenever a new node is focused.
   * This populates the forms with the specific values returned from each instance of
   * the default values function for the given form adapter.
   */
  const prevFocusedActionPublicId = useRef<string>(focusedAction?.publicId);
  const prevTab = useRef<NodeEditorTab>(tab);
  // workaround to solve for race conditions when creating PA - action to open drawer and focus node dispatched before workflowProps is updated, causing empty form fields
  const prevPublishedAttributes = useRef<PublishedAttribute[]>(publishedAttributes);
  // Track whether the action has a PA to detect when an action changes to/from PA.
  const prevHasPublishedAttribute = useRef<boolean>(
    Boolean(
      focusedAction?.input?.publishedAttributeToken
      || focusedAction?.output?.publishedAttributeToken,
    ),
  );

  React.useEffect(() => {
    if (!focusedAction || !formControl) {
      return;
    }

    const currentHasPublishedAttribute = Boolean(
      focusedAction.input?.publishedAttributeToken || focusedAction.output?.publishedAttributeToken,
    );

    const hasTabChangedOnSameAction = (
      prevFocusedActionPublicId.current === focusedAction.publicId && prevTab.current !== tab
    );
    const hasFocusedDifferentAction = (
      prevFocusedActionPublicId.current !== focusedAction.publicId
    );
    // this only works if # of published attributes change
    const havePublishedAttributesChanged = (
      prevPublishedAttributes.current.length !== publishedAttributes.length
    );
    // Detect when an action changes state (no PA => PA or PA => no PA)
    const hasPublishedAttributeStateChanged = (
      prevHasPublishedAttribute.current !== currentHasPublishedAttribute
    );

    const hasDefinedFormValues = (
      Object.values(formControl?.getValues() ?? {}).some(value => value !== undefined)
    );

    if (
      hasTabChangedOnSameAction
      || hasFocusedDifferentAction
      || havePublishedAttributesChanged
      || hasPublishedAttributeStateChanged
      || !hasDefinedFormValues
    ) {
      const formAdapter = getFormAdapter(focusedAction, tab);
      formControl.reset(formAdapter.getDefaultFormValues(focusedAction, workflowProps));
      prevFocusedActionPublicId.current = focusedAction.publicId;
      prevTab.current = tab;
      prevPublishedAttributes.current = publishedAttributes;
      prevHasPublishedAttribute.current = currentHasPublishedAttribute;
    }
  }, [focusedAction, formControl, tab, workflowProps, publishedAttributes]);

  return formControl;
}

